// §9.17/D139 — PURE Grid-Geometrie für den manuellen Grid-Dialog (Stufe C) + das Live-Overlay.
// Kein React/DOM ⇒ in vitest testbar. Zellkante = Sheet-Maß / (cols|rows) — ganzzahlig
// (Spiegel ops.spritesheet_unpack: cell_w = W // cols, cell_h = H // rows).

export interface GridSpec {
  cols: number;
  rows: number;
  frameCount: number;
}

/** cols/rows/frameCount auf gültige Bereiche klemmen (frameCount ∈ [1, cols×rows]). */
export function clampGrid(cols: number, rows: number, frameCount: number, maxDim = 64): GridSpec {
  const c = Math.max(1, Math.min(Math.trunc(cols), maxDim));
  const r = Math.max(1, Math.min(Math.trunc(rows), maxDim));
  const fc = Math.max(1, Math.min(Math.trunc(frameCount), c * r));
  return { cols: c, rows: r, frameCount: fc };
}

/** Ganzzahlige Zellmaße eines Sheets (W×H) für ein cols×rows-Gitter (ops.spritesheet_unpack-Spiegel). */
export function cellSize(width: number, height: number, cols: number, rows: number): { cellW: number; cellH: number } {
  const c = Math.max(1, Math.trunc(cols));
  const r = Math.max(1, Math.trunc(rows));
  return { cellW: Math.floor(Math.max(0, width) / c), cellH: Math.floor(Math.max(0, height) / r) };
}

/**
 * Overlay-Linien (innere Gitter-Grenzen) in Sheet-Pixel-Koordinaten — die Ränder (0/W, 0/H)
 * werden NICHT zurückgegeben (das ist der Rahmen). Für ein Canvas-Overlay über dem Sheet.
 */
export function gridLines(width: number, height: number, cols: number, rows: number): { vertical: number[]; horizontal: number[] } {
  const { cellW, cellH } = cellSize(width, height, cols, rows);
  const vertical: number[] = [];
  const horizontal: number[] = [];
  for (let i = 1; i < Math.max(1, Math.trunc(cols)); i++) vertical.push(i * cellW);
  for (let i = 1; i < Math.max(1, Math.trunc(rows)); i++) horizontal.push(i * cellH);
  return { vertical, horizontal };
}

/** Zellen-Rechteck (row-major) eines Frame-Index im Sheet — für Highlights/Belegungs-Anzeige. */
export function cellRect(
  index: number,
  width: number,
  height: number,
  cols: number,
  rows: number,
): { x: number; y: number; w: number; h: number } {
  const c = Math.max(1, Math.trunc(cols));
  const { cellW, cellH } = cellSize(width, height, c, rows);
  const i = Math.max(0, Math.trunc(index));
  const col = i % c;
  const row = Math.floor(i / c);
  return { x: col * cellW, y: row * cellH, w: cellW, h: cellH };
}

/** Anzahl ungenutzter (leerer) End-Zellen bei gegebenem Grid + frameCount. */
export function trailingEmptyCells(spec: GridSpec): number {
  return Math.max(0, spec.cols * spec.rows - spec.frameCount);
}

/**
 * Rechtecke der leeren End-Zellen (Indizes frameCount … cols×rows−1) in Sheet-Pixeln — für das
 * Overlay über dem Sheet-Preview (die nicht-belegten Zellen werden abgedunkelt). Reihenfolge
 * row-major (== ops.spritesheet_unpack). Bei frameCount == cols×rows leer.
 */
export function emptyCellRects(
  spec: GridSpec,
  width: number,
  height: number,
): { x: number; y: number; w: number; h: number }[] {
  const rects: { x: number; y: number; w: number; h: number }[] = [];
  const total = spec.cols * spec.rows;
  for (let i = spec.frameCount; i < total; i++) {
    rects.push(cellRect(i, width, height, spec.cols, spec.rows));
  }
  return rects;
}
